import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]:not([hidden])',
  'button:not([disabled]):not([hidden])',
  'input:not([disabled]):not([type="hidden"]):not([hidden])',
  'select:not([disabled]):not([hidden])',
  'textarea:not([disabled]):not([hidden])',
  '[tabindex]:not([tabindex="-1"]):not([hidden])',
].join(',');

/**
 * Traps focus inside an open overlay and restores it on close.
 *
 * Not one of the legacy app's modals was a real dialog — no focus trap, no Escape, no
 * focus restore — so keyboard and screen-reader users could tab straight out into the
 * page behind an open modal. Every overlay in the rebuild uses this.
 */
export function useFocusTrap(isOpen: boolean, onClose: () => void, closeOnEscape = true) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Keep the latest onClose / closeOnEscape in refs so the setup effect can
  // depend on `isOpen` ALONE. Depending on `onClose` (which callers routinely
  // pass as a fresh inline arrow) made the effect re-run on every parent
  // re-render — e.g. a polling query on the page behind the modal — and each
  // re-run re-ran the initial `.focus()`, yanking focus to the first focusable
  // element (the close button) mid-typing. Reading these from refs keeps the
  // handlers current without re-arming the trap.
  const onCloseRef = useRef(onClose);
  const closeOnEscapeRef = useRef(closeOnEscape);
  useEffect(() => {
    onCloseRef.current = onClose;
    closeOnEscapeRef.current = closeOnEscape;
  });

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const container = containerRef.current;
    if (container) {
      const first = container.querySelector<HTMLElement>(FOCUSABLE);
      // Fall back to the panel itself so focus never stays behind the overlay.
      (first ?? container).focus();
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        // While a submit is in flight the overlay is not dismissible, and Escape must
        // respect that too — otherwise the dialog closes and the success toast for the
        // "cancelled" form still arrives.
        if (closeOnEscapeRef.current) onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const node = containerRef.current;
      if (!node) return;

      const focusable = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.checkVisibility?.() ?? true,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    // Lock scroll without letting nested overlays fight over the value.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus();
    };
  }, [isOpen]);

  return containerRef;
}
